import { useEffect, useMemo, useState } from "react";
import BudgetModulePageShell from "../components/BudgetModulePageShell";
import { AppLoader, ButtonLoader } from "../components/Loaders";
import { getChangeRequests } from "../api/budgetPortal";
import { useAuth } from "../state/AuthContext";
import { useBudgetSocket } from "../state/BudgetSocketContext";
import { useBudgetModuleYears } from "../hooks/useBudgetModuleYears";
import { isAccountantUser } from "../utils/userRole";
import { DEPARTMENT_LABELS, formatAmount } from "../utils/budgetWorkspace";

const formatDepartments = (departments = []) => {
  return departments
    .map((departmentKey) => DEPARTMENT_LABELS[departmentKey] ?? departmentKey)
    .join(", ");
};

const formatRequestType = (request = {}) => {
  if (request.request_type === "adjustment") {
    return request.scope === "wholeBudget"
      ? "Whole budget adjustment"
      : request.scope === "singleLineItem"
        ? "Single line item adjustment"
        : "Department adjustment";
  }

  return "Manual budget edit";
};

const getChangeSummary = (request = {}) => {
  const proposedRows = Array.isArray(request.proposed_rows)
    ? request.proposed_rows
    : [];
  const metadata =
    request.metadata && typeof request.metadata === "object"
      ? request.metadata
      : {};
  const financialYear = request.financial_year ? ` for ${request.financial_year}` : "";

  if (request.request_type === "adjustment") {
    const percentage = metadata?.percentage;
    const scopeLabel =
      request.scope === "wholeBudget"
        ? "whole budget"
        : request.scope === "wholeDepartment"
          ? `whole ${DEPARTMENT_LABELS[metadata.department_key] ?? metadata.department_key ?? "department"}`
          : request.scope === "department"
            ? `${DEPARTMENT_LABELS[metadata.department_key] ?? metadata.department_key ?? "department"}`
            : metadata.line_item_title || proposedRows[0]?.viDescription || proposedRows[0]?.codeDescription || proposedRows[0]?.viCode;

    return `${percentage ?? "-"}% adjustment across ${scopeLabel}${financialYear}`;
  }

  const row = proposedRows[0] ?? {};
  return `${metadata.line_item_title || row.viDescription || row.codeDescription || row.viCode}: ${formatAmount(
    metadata.previous_value,
  )} => ${formatAmount(metadata.next_value ?? row.estimatedCurrentYear)}${financialYear}`;
};

const BudgetModuleChangeRequestsPage = () => {
  const { token, user } = useAuth();
  const budgetSocket = useBudgetSocket();
  const isAccountant = isAccountantUser(user);
  const { yearCards, selectedYear, setSelectedYear, loading, error } =
    useBudgetModuleYears(token);
  const [requests, setRequests] = useState([]);
  const [pageError, setPageError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [busyRequestId, setBusyRequestId] = useState("");
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [responseRequest, setResponseRequest] = useState(null);
  const [responseAction, setResponseAction] = useState("");
  const [responseRemark, setResponseRemark] = useState("");
  const [responseRemarkError, setResponseRemarkError] = useState("");

  const visibleRequests = useMemo(() => requests, [requests]);

  useEffect(() => {
    if (!selectedYear) {
      setRequests([]);
      return;
    }

    const loadRequests = async () => {
      setRequestsLoading(true);
      setPageError("");

      try {
        const response = await getChangeRequests(token, selectedYear);
        setRequests(response?.data ?? []);
      } catch (requestError) {
        setPageError(
          requestError?.response?.data?.message ||
            requestError.message ||
            "Unable to load change requests.",
        );
      }

      setRequestsLoading(false);
    };

    loadRequests();
  }, [budgetSocket?.changeRequestRevision, selectedYear, token]);

  const handleWithdraw = async (request) => {
    const confirmed = window.confirm("Withdraw this pending change request?");

    if (!confirmed) {
      return;
    }

    setBusyRequestId(`${request.id}`);
    setPageError("");
    setActionMessage("");

    try {
      await budgetSocket.withdrawChangeRequest(request.id);
      setActionMessage("Change request withdrawn.");
    } catch (withdrawError) {
      setPageError(withdrawError.message || "Unable to withdraw change request.");
    }

    setBusyRequestId("");
  };

  const openResponseModal = (request, action) => {
    setResponseRequest(request);
    setResponseAction(action);
    setResponseRemark("");
    setResponseRemarkError("");
  };

  const handleRespond = async (event) => {
    event.preventDefault();

    const remark = responseRemark.trim();
    if (remark.length < 3 || remark.length > 500) {
      setResponseRemarkError("Enter a remark between 3 and 500 characters.");
      return;
    }

    setBusyRequestId(`${responseRequest.id}-${responseAction}`);
    setPageError("");
    setActionMessage("");
    setResponseRemarkError("");

    try {
      await budgetSocket.respondChangeRequest(
        responseRequest.id,
        responseAction,
        remark,
      );
      setActionMessage(
        responseAction === "accept"
          ? "Change request accepted."
          : "Change request rejected.",
      );
      setResponseRequest(null);
      setResponseAction("");
      setResponseRemark("");
    } catch (responseError) {
      setPageError(responseError.message || "Unable to respond to change request.");
    }

    setBusyRequestId("");
  };

  return (
    <BudgetModulePageShell
      title="Budget Module Change Request"
      description=""
      yearCards={yearCards}
      selectedYear={selectedYear}
      onSelectYear={setSelectedYear}
    >
      {error ? <div className="alert alert--error">{error}</div> : null}
      {pageError ? <div className="alert alert--error">{pageError}</div> : null}
      {actionMessage ? (
        <div className="alert alert--success">{actionMessage}</div>
      ) : null}

      {loading || requestsLoading ? (
        <AppLoader label="Loading change requests..." />
      ) : !selectedYear ? (
        <div className="bp-emptyState">No budget years are available yet.</div>
      ) : visibleRequests.length === 0 ? (
        <div className="bp-emptyState">No change requests for this year.</div>
      ) : (
        <div className="change-request-list">
          {visibleRequests.map((request) => (
            <div className="change-request-card" key={request.id}>
              <div className="change-request-card__header">
                <div>
                  <p className="change-request-card__eyebrow">
                    {formatRequestType(request)}
                  </p>
                  <h3>{getChangeSummary(request)}</h3>
                </div>
                <span
                  className={`change-request-status change-request-status--${request.status}`}
                >
                  {request.status}
                </span>
              </div>

              <div className="change-request-card__meta">
                <span>{request.financial_year}</span>
                <span>{formatDepartments(request.target_departments)}</span>
              </div>

              {request.remark ? (
                <div className="change-request-remark">
                  <strong>Request remark</strong>
                  <p>{request.remark}</p>
                </div>
              ) : null}

              <div className="change-request-approvals">
                {Object.entries(request.approvals ?? {}).map(
                  ([departmentKey, approval]) => (
                    <div className="change-request-approval" key={departmentKey}>
                      <div>
                        <span>{DEPARTMENT_LABELS[departmentKey] ?? departmentKey}</span>
                        {approval.remark ? <small>{approval.remark}</small> : null}
                      </div>
                      <strong>{approval.status}</strong>
                    </div>
                  ),
                )}
              </div>

              {request.status === "pending" ? (
                <div className="change-request-actions">
                  {!isAccountant ? (
                    <button
                      type="button"
                      disabled={
                        !budgetSocket?.connected ||
                        busyRequestId === `${request.id}`
                      }
                      onClick={() => handleWithdraw(request)}
                    >
                      {busyRequestId === `${request.id}` ? (
                        <ButtonLoader label="Withdrawing..." />
                      ) : (
                        "Withdraw"
                      )}
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        disabled={
                          !budgetSocket?.connected ||
                          busyRequestId === `${request.id}-accept`
                        }
                        onClick={() => openResponseModal(request, "accept")}
                      >
                        {busyRequestId === `${request.id}-accept` ? (
                          <ButtonLoader label="Accepting..." />
                        ) : (
                          "Accept"
                        )}
                      </button>
                      <button
                        type="button"
                        className="change-request-actions__reject"
                        disabled={
                          !budgetSocket?.connected ||
                          busyRequestId === `${request.id}-reject`
                        }
                        onClick={() => openResponseModal(request, "reject")}
                      >
                        {busyRequestId === `${request.id}-reject` ? (
                          <ButtonLoader label="Rejecting..." />
                        ) : (
                          "Reject"
                        )}
                      </button>
                    </>
                  )}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {responseRequest ? (
        <div className="budget-modalOverlay">
          <form className="budget-remarkModal" onSubmit={handleRespond}>
            <h3>
              {responseAction === "accept" ? "Accept" : "Reject"} change request
            </h3>
            <p>Add a remark for this response.</p>
            <textarea
              value={responseRemark}
              maxLength={500}
              placeholder="Write your response remark"
              onChange={(event) => setResponseRemark(event.target.value)}
              autoFocus
            />
            {responseRemarkError ? (
              <div className="web-inlineError">{responseRemarkError}</div>
            ) : null}
            <div className="budget-remarkModal__actions">
              <button
                type="button"
                className="button-secondary"
                disabled={Boolean(busyRequestId)}
                onClick={() => {
                  setResponseRequest(null);
                  setResponseAction("");
                  setResponseRemark("");
                  setResponseRemarkError("");
                }}
              >
                Cancel
              </button>
              <button type="submit" disabled={Boolean(busyRequestId)}>
                {busyRequestId ? (
                  <ButtonLoader label="Saving..." />
                ) : (
                  responseAction === "accept" ? "Accept" : "Reject"
                )}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </BudgetModulePageShell>
  );
};

export default BudgetModuleChangeRequestsPage;
